import { useCallback, useMemo, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Blog } from '../../interfaces/Blog/Blog';
import { generateCoverImage } from '../../api/Blog';
import { sharedColors, sharedStyles } from '../../utils/Style';

type GenerateBlogCoverImageDialogProps = {
  blog: Blog;
  open: boolean;
  onClose: () => void;
  onGenerate: (coverImage: File) => void;
};

export const GenerateBlogCoverImageDialog = ({
  blog,
  open,
  onClose,
  onGenerate,
}: GenerateBlogCoverImageDialogProps) => {
  const [image, setImage] = useState<File | undefined>(undefined);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = useCallback(() => {
    setLoading(true);
    generateCoverImage(blog, additionalPrompt)
      .then(setImage)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [blog, additionalPrompt]);

  const imageURL = useMemo(
    () => (image ? URL.createObjectURL(image) : undefined),
    [image],
  );

  return (
    <Dialog maxWidth='md' open={open} onClose={onClose}>
      <DialogTitle>Resim Oluştur</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: 450,
        }}
      >
        {!!image && !loading ? (
          <Box
            component='img'
            src={imageURL}
            alt='Resim'
            sx={{
              width: 450,
              height: 300,
              objectFit: 'contain',
              borderColor: sharedColors.gray3,
              borderWidth: 1,
              borderStyle: 'solid',
            }}
          />
        ) : (
          <Paper
            sx={{
              width: 450,
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: sharedColors.gray3,
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <ImageIcon fontSize='large' />
            )}
          </Paper>
        )}

        <TextField
          label='Ek notlar'
          variant='outlined'
          multiline
          size='small'
          minRows={3}
          disabled={loading}
          value={additionalPrompt}
          onChange={(e) => setAdditionalPrompt(e.target.value)}
        />

        <Button
          variant='outlined'
          color='primary'
          startIcon={<AutoAwesomeIcon />}
          disabled={loading}
          onClick={handleGenerate}
          sx={{ ...sharedStyles.buttonText, alignSelf: 'center' }}
        >
          Oluştur
        </Button>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 1.5 }}>
        <Button
          variant='contained'
          color='inherit'
          onClick={onClose}
          sx={sharedStyles.buttonText}
        >
          İptal
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!image}
          onClick={() => {
            if (image) {
              onGenerate(image);
            }
            onClose();
          }}
          sx={sharedStyles.buttonText}
        >
          Onayla
        </Button>
      </DialogActions>
    </Dialog>
  );
};
